import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import React, { useState, useEffect, useRef } from "react";
import { Card, Button } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import PageTitle from "../../layouts/PageTitle";
import BilingualText, {
	BilingualLabel,
	useBilingualPlaceholder,
} from "../../components/BilingualText/BilingualText";
import { PartnerSearchSelect } from "../../components/SearchSelects";
import TransparentLoader from "../../components/Loaders/TransparentLoader";
import { getCurrentCompanyId, getCurrentBranchId } from "../../../util/contextHelper";
import swal from "sweetalert";
import useApiAlert from "../../hooks/useApiAlert";
import useStickyAction from "../../hooks/useStickyAction";
import {
	getDepositorByIdAction,
	clearCurrentDepositor,
	clearErrors,
	updateDepositorAction,
	createDepositorAction,
} from "../../../store/actions/DepositorsActions";

const AddUpdateDepositor = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { id } = useParams();
	const isEdit = Boolean(id);
	const { t } = useTranslation();

	const namePlaceholder = useBilingualPlaceholder("depositors.name");
	const emailPlaceholder = useBilingualPlaceholder("depositors.email");
	const phonePlaceholder = useBilingualPlaceholder("depositors.phoneNo");
	const addressPlaceholder = useBilingualPlaceholder("depositors.address");
	const cityPlaceholder = useBilingualPlaceholder("depositors.city");
	const districtPlaceholder = useBilingualPlaceholder("depositors.district");
	const statePlaceholder = useBilingualPlaceholder("depositors.state");

	const contextCompanyId = getCurrentCompanyId();
	const contextBranchId = getCurrentBranchId();

	const { data: currentDepositor, loading, error, validationErrors } = useSelector(
		(state) => state.depositors
	);

	const { ApiAlert, showError, show } = useApiAlert();
	const reduxErrorRef = useRef(null);

	const { sentinelRef, cardRef, style, isFixed } = useStickyAction();

	useEffect(() => {
		if (isEdit) dispatch(getDepositorByIdAction(id));
		return () => {
			dispatch(clearCurrentDepositor());
			dispatch(clearErrors());
		};
	}, [dispatch, id, isEdit]);

	const [initialValues, setInitialValues] = useState({
		name: "",
		address: "",
		city: "",
		district: "",
		state: "",
		phoneNo: "",
		email: "",
		companyId: contextCompanyId || "",
		status: "Active",
	});

	useEffect(() => {
		if (isEdit && currentDepositor && Object.keys(currentDepositor).length) {
			setInitialValues({
				name: currentDepositor.name || "",
				address: currentDepositor.address || "",
				city: currentDepositor.city || "",
				district: currentDepositor.district || "",
				state: currentDepositor.state || "",
				phoneNo: currentDepositor.phoneNo || currentDepositor.phoneNumber || "",
				email: currentDepositor.email || "",
				companyId: currentDepositor.companyId || contextCompanyId || "",
				status: currentDepositor.status || "Active",
			});
		}
	}, [currentDepositor, isEdit, contextCompanyId]);

	const validationSchema = Yup.object({
		name: Yup.string().required(t("validation.required", { field: t("depositors.name") })),
		email: Yup.string().email(t("validation.email")),
		phoneNo: Yup.string()
			.matches(/^[0-9]{10}$/, t("validation.phone"), { excludeEmptyString: true })
			.nullable(),
		companyId: Yup.string().required(t("validation.required", { field: t("depositors.company") })), 
		status: Yup.string().oneOf(["Active", "Inactive"], t("validation.statusValid")),
	});

	const getFieldValidationError = (fieldName) => {
		if (!validationErrors || !Array.isArray(validationErrors)) return null;
		const fieldError = validationErrors.find((err) => err.path === fieldName);
		return fieldError ? fieldError.msg : null;
	};

	const handleSubmit = (values, { setSubmitting }) => {
		const payload = {
			name: values.name?.trim(),
			address: values.address?.trim() || null,
			city: values.city || null,
			district: values.district || null,
			state: values.state || null,
			phoneNo: values.phoneNo || null,
			email: values.email || null,
			companyId: values.companyId,
			status: values.status,
		};

		if (isEdit) {
			dispatch(
				updateDepositorAction(id, payload, (err, data, status) => {
					if (status) {
						swal({ title: "Success", text: "Depositor updated", icon: "success" }).then(() => navigate("/depositors"));
					} else if (err) {
						showError(err.message || "Failed to update");
					}
				})
			);
		} else {
			dispatch(
				createDepositorAction(payload, (err, data, status) => {
					if (status) {
						swal({ title: "Success", text: "Depositor created", icon: "success" }).then(() => navigate("/depositors"));
					} else if (err) {
						showError(err.message || "Failed to create");
					}
				})
			);
		}
		setSubmitting(false);
	};

	return (
		<>
			<PageTitle activeMenu={isEdit ? "Edit Depositor" : "Add Depositor"} motherMenu="Finance" />

			<div className="row stickey-card-holder">
				<div className="col-lg-12">
					<div className="card min-height-auto mb-2">
						<div className="card-header">
							<h4 className="card-title">
								<BilingualText translationKey={isEdit ? "depositors.editDepositor" : "depositors.addDepositor"} />
							</h4>
						</div>
					</div>
					{ApiAlert && <ApiAlert />}
					{error && <div className="alert alert-danger">{typeof error === 'string' ? error : error.message}</div>}

					<div style={{ position: "relative" }}>
						<TransparentLoader show={loading && isEdit} />
						<Formik initialValues={initialValues} validationSchema={validationSchema} enableReinitialize onSubmit={handleSubmit}>
							{({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, resetForm }) => (
								<form onSubmit={handleSubmit}>
									<Card className="mb-2  border-light">
										<Card.Header>
											<div className="d-flex align-items-center text-start fs-16">
												<i className="fas fa-user me-2" />
												<BilingualText translationKey="depositors.basicInformation" />
											</div>
										</Card.Header>
										<Card.Body>
											<div className="row">
												<div className="col-md-4 mb-3">
													<BilingualLabel translationKey="depositors.name" required={true} />
													<input name="name" type="text" className={`form-control ${(touched.name && errors.name) || getFieldValidationError("name") ? "is-invalid" : ""}`} value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder={namePlaceholder} />
													{(touched.name && errors.name) && <div className="invalid-feedback">{errors.name}</div>}
													{getFieldValidationError("name") && <div className="invalid-feedback d-block">{getFieldValidationError("name")}</div>}
												</div>

												<div className="col-md-4 mb-3">
													<BilingualLabel translationKey="depositors.email" />
													<input name="email" type="email" className={`form-control ${(touched.email && errors.email) || getFieldValidationError("email") ? "is-invalid" : ""}`} value={values.email} onChange={handleChange} onBlur={handleBlur} placeholder={emailPlaceholder} />
													{(touched.email && errors.email) && <div className="invalid-feedback">{errors.email}</div>}
													{getFieldValidationError("email") && <div className="invalid-feedback d-block">{getFieldValidationError("email")}</div>}
												</div>

												<div className="col-md-4 mb-3">
													<BilingualLabel translationKey="depositors.phoneNo" />
													<input name="phoneNo" type="tel" className={`form-control ${(touched.phoneNo && errors.phoneNo) || getFieldValidationError("phoneNo") ? "is-invalid" : ""}`} value={values.phoneNo} onChange={(e) => { const value = e.target.value.replace(/\D/g, '').slice(-10); handleChange({ target: { name: 'phoneNo', value } }); }} onBlur={handleBlur} placeholder={phonePlaceholder} maxLength={10} />
													{(touched.phoneNo && errors.phoneNo) && <div className="invalid-feedback">{errors.phoneNo}</div>}
													{getFieldValidationError("phoneNo") && <div className="invalid-feedback d-block">{getFieldValidationError("phoneNo")}</div>}
												</div>

													<input type="hidden" name="companyId" value={values.companyId} />

													<div className="col-md-4 mb-3">
														<BilingualLabel translationKey="depositors.status" required={true} />
														<select name="status" className={`form-select form-control ${(touched.status && errors.status) || getFieldValidationError("status") ? "is-invalid" : ""}`} value={values.status} onChange={handleChange} onBlur={handleBlur}>
															<option value="Active">Active</option>
															<option value="Inactive">Inactive</option>
														</select>
														{(touched.status && errors.status) && <div className="invalid-feedback">{errors.status}</div>}
														{getFieldValidationError("status") && <div className="invalid-feedback d-block">{getFieldValidationError("status")}</div>}
													</div>
											</div>
										</Card.Body>
									</Card>

									<Card className="mb-2  border-light">
										<Card.Header>
											<div className="d-flex align-items-center text-start fs-16">
												<i className="fas fa-map-marker-alt me-2" />
												<BilingualText translationKey="depositors.addressInformation" />
											</div>
										</Card.Header>
										<Card.Body>
											<div className="row">
												<div className="col-6 mb-3">
													<BilingualLabel translationKey="depositors.address" />
													<textarea name="address" rows={5} className={`form-control ${(touched.address && errors.address) || getFieldValidationError("address") ? "is-invalid" : ""}`} value={values.address} onChange={handleChange} onBlur={handleBlur} placeholder={addressPlaceholder} />
													{(touched.address && errors.address) && <div className="invalid-feedback">{errors.address}</div>}
													{getFieldValidationError("address") && <div className="invalid-feedback d-block">{getFieldValidationError("address")}</div>}
												</div>
												<div className="col-6 mb-3">
													<div className="row">
														<div className="col-md-6 mb-3">
															<BilingualLabel translationKey="depositors.city" />
															<input name="city" type="text" className={`form-control ${(touched.city && errors.city) || getFieldValidationError("city") ? "is-invalid" : ""}`} value={values.city} onChange={handleChange} onBlur={handleBlur} placeholder={cityPlaceholder} />
															{(touched.city && errors.city) && <div className="invalid-feedback">{errors.city}</div>}
															{getFieldValidationError("city") && <div className="invalid-feedback d-block">{getFieldValidationError("city")}</div>}
														</div>
														<div className="col-md-6 mb-3">
															<BilingualLabel translationKey="depositors.district" />
															<input name="district" type="text" className={`form-control ${(touched.district && errors.district) || getFieldValidationError("district") ? "is-invalid" : ""}`} value={values.district} onChange={handleChange} onBlur={handleBlur} placeholder={districtPlaceholder} />
															{(touched.district && errors.district) && <div className="invalid-feedback">{errors.district}</div>}
															{getFieldValidationError("district") && <div className="invalid-feedback d-block">{getFieldValidationError("district")}</div>}
														</div>
														<div className="col-md-6 mb-3">
															<BilingualLabel translationKey="depositors.state" />
															<input name="state" type="text" className={`form-control ${(touched.state && errors.state) || getFieldValidationError("state") ? "is-invalid" : ""}`} value={values.state} onChange={handleChange} onBlur={handleBlur} placeholder={statePlaceholder} />
															{(touched.state && errors.state) && <div className="invalid-feedback">{errors.state}</div>}
															{getFieldValidationError("state") && <div className="invalid-feedback d-block">{getFieldValidationError("state")}</div>}
														</div>
													</div>
												</div>
											</div>
										</Card.Body>
									</Card>

										<div ref={sentinelRef} />
										<Card ref={cardRef} style={style} className="mb-2 sticky-action-card border-light actions p-2 min-height-auto">
											<div className="d-flex justify-content-between">
												<Button
													variant="danger"
													onClick={() => navigate("/depositors")}
													className="text-white d-flex align-items-center btn-rounded"
												>
													<i className="fas fa-arrow-left me-2"></i>
													<BilingualText translationKey="common.cancel" />
												</Button>
												<div className="d-flex gap-2">
													{!isEdit && (
														<Button
															variant="secondary"
															type="reset"
															className="text-white d-flex align-items-center btn-rounded"
															onClick={() => resetForm()}
															disabled={isSubmitting}
														>
															<i className="fas fa-redo me-2"></i>
															<BilingualText translationKey="common.reset" />
														</Button>
													)}
													<Button
														variant="primary"
														type="submit"
														disabled={isSubmitting}
														className="text-white d-flex align-items-center btn-rounded"
													>
														{isSubmitting ? (
															<>
																<span className="spinner-border spinner-border-sm me-2" />
																<BilingualText translationKey={isEdit ? "common.updating" : "common.creating"} />
															</>
														) : (
															<>
																<BilingualText translationKey={isEdit ? "depositors.editDepositor" : "depositors.addDepositor"} />
																<i className="fas fa-check ms-2"></i>
															</>
														)}
													</Button>
												</div>
											</div>
										</Card>
								</form>
							)}
						</Formik>
					</div>
				</div>
			</div>
		</>
	);
};

export default AddUpdateDepositor;